/**
 * `WordPressInstallPanel` — body of the `wordpress` install-method
 * tab inside `InstallMethodTabs` (ADR-0042 §2). WordPress and
 * WooCommerce used to share this tab under a "WordPress / WooCommerce"
 * label; founder live-testing (2026-07-08) surfaced that the combined
 * tab conflates two separate installation paths — a cart-free WP site
 * has nothing to connect, and a WooCommerce merchant needs an explicit
 * two-step (paste snippet → connect REST API). The tabs are now split:
 * this panel is script-only, and `WooCommerceInstallPanel` owns the
 * two-step Woo flow.
 *
 * There IS a Sill WordPress plugin (ADR-0188) and — post ADR-0193 — it
 * is the RECOMMENDED WordPress install: one upload serves the agent card
 * + proof file and logs traffic server-side, no script paste (the plugin
 * is complete on its own; ADR-0193 §9.4). The `<script>` snippet is
 * demoted to a "can't install a plugin" fallback under a disclosure — it
 * is an analytics enhancement, not a requirement. The rotate affordance
 * for a session with no visible `site_key` is preserved inside the
 * snippet block. Shared with `WooCommerceInstallPanel` via
 * `WordPressScriptSnippetBlock` — the two panels MUST NOT drift on
 * snippet, copy, or detector.
 *
 * Verify: the plugin serves `/.well-known/sill-proof.txt`, so the
 * standard homepage/fallback proof-check hits it directly. The panel
 * renders an inline Verify button next to the plugin download that
 * drives the shared `useProofCheck` state the composing step passed in
 * — mirrors the script-tag tab (ADR-0050 §1.2 paste-once / verify-once).
 * Previously missing: the panel never surfaced any Verify affordance
 * and `ProofStatusPanel` hides its own Verify when a parent-mounted
 * state is injected, so a plugin-only install was non-verifiable from
 * the WordPress tab.
 */

import { ArrowRight, Check, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';

import { useC } from '../../lib/theme.js';
import type { UseProofCheckState } from '../../hooks/useProofCheck.js';
import { AgentReadyPluginDownload } from './AgentReadyPluginDownload.js';

const sansFont = "'Geist', sans-serif";

export interface WordPressInstallPanelProps {
  onInstalled?: () => void;
  /** ADR-0212 slice C — when set, Step 1 is the Worker (orange-clouded site). */
  workerStep?: ReactNode;
  /** Shown under the plugin block when the Worker is available but the merchant chose the plugin. */
  onPreferWorker?: () => void;
  /** Shared proof-check state from the composing step. When present the
   *  WordPress panel renders an inline Verify button bound to this
   *  state — mirrors `ScriptTagInstallPanel` (ADR-0050 §1.2). The
   *  server's homepage check reads `/.well-known/sill-proof.txt`, which
   *  the plugin serves verbatim, so no snippet paste is required. */
  proofCheck?: UseProofCheckState;
  /** Switch the install picker to the Script tag tab (founder 2026-07-29:
   *  the "can't install a plugin" fallback should route to the ONE script
   *  surface, not re-render the snippet inline). Omitted in the no-tabs
   *  model presentation, where the manual script path renders separately. */
  onSwitchToScriptTab?: () => void;
}

export function WordPressInstallPanel({
  onSwitchToScriptTab,
  workerStep,
  onPreferWorker,
  proofCheck,
}: WordPressInstallPanelProps): JSX.Element {
  const C = useC();
  // Mirrors `ScriptTagInstallPanel`'s gate verbatim (ADR-0050 §1.2) so a
  // merchant can only click Verify once the proof token has loaded and
  // the panel has an active site.
  const verifyDisabled =
    proofCheck === undefined
    || proofCheck.checking
    || proofCheck.verified
    || proofCheck.loading
    || proofCheck.data === null
    || proofCheck.site === null;
  return (
    <div>
      {/* ADR-0193 — recommended one-step install: the plugin serves the
          card + proof + logs traffic. No paste needed. ADR-0212: behind
          Cloudflare the Worker takes Step 1 instead. Either way the WP
          panel shows only ONE step, so the numbered badge drops
          (founder 2026-10-21). */}
      {workerStep !== undefined ? workerStep : (
        <AgentReadyPluginDownload kicker="STEP 1 · INSTALL THE SILL PLUGIN" showStepBadge={false} />
      )}
      {/* Inline Verify — mirrors the script-tag tab (ADR-0050 §1.2).
          Previously missing: the WordPress panel threaded `proofCheck`
          but never rendered a control, leaving no way to confirm the
          plugin install. The server check reads `/.well-known/sill-
          proof.txt` which the plugin serves — no additional paste. */}
      {proofCheck !== undefined ? (
        <div
          className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-4"
          data-testid="wp-verify-row"
        >
          <button
            type="button"
            onClick={() => void proofCheck.verify()}
            disabled={verifyDisabled}
            className="flex items-center justify-center gap-2 px-5 py-2.5"
            style={{
              background: proofCheck.verified ? C.limeDim : C.lime,
              color: proofCheck.verified ? C.limeFg : C.ink,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 14,
              borderRadius: 2,
              opacity: verifyDisabled && !proofCheck.verified ? 0.6 : 1,
              border: proofCheck.verified ? `1px solid ${C.limeFg}` : 'none',
            }}
            aria-label={proofCheck.verified ? 'Domain verified' : 'Verify domain'}
            data-testid="wp-verify-button"
          >
            {proofCheck.checking ? (
              <>
                <RefreshCw
                  size={15}
                  style={{ animation: 'spin 1.4s linear infinite' }}
                />
                Checking…
              </>
            ) : proofCheck.verified ? (
              <>
                <Check size={15} /> Domain verified
              </>
            ) : (
              <>
                Verify <ArrowRight size={15} />
              </>
            )}
          </button>
        </div>
      ) : null}
      {/* Alternatives — one row BELOW the primary action, 12px gap. Stay
          enabled during a check (plain navigation, no spinner). */}
      {onPreferWorker !== undefined || onSwitchToScriptTab !== undefined ? (
        <div
          className="flex flex-wrap items-center"
          style={{ gap: '0 10px', marginTop: 12 }}
        >
          {onPreferWorker !== undefined ? (
            <button
              type="button"
              onClick={onPreferWorker}
              className="inline-flex items-center gap-1"
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: sansFont, fontSize: 13, color: C.muted }}
              data-testid="wp-prefer-worker"
            >
              On Cloudflare?{' '}
              <span style={{ color: C.text, fontWeight: 500 }}>Use the Worker</span>
              <ArrowRight size={12} />
            </button>
          ) : null}
          {onPreferWorker !== undefined && onSwitchToScriptTab !== undefined ? (
            <span style={{ fontFamily: sansFont, fontSize: 13, color: C.dim }}>·</span>
          ) : null}
          {onSwitchToScriptTab !== undefined ? (
            <button
              type="button"
              onClick={onSwitchToScriptTab}
              className="inline-flex items-center gap-1"
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: sansFont, fontSize: 13, color: C.muted }}
              data-testid="wp-fallback-script-tab"
            >
              Can&apos;t use a plugin?{' '}
              <span style={{ color: C.text, fontWeight: 500 }}>Use the script tag</span>
              <ArrowRight size={12} />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
